import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useContext } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LoadingContext } from "../../../../contexts/Loading";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { REGISTER } from "../../../../enums/ApiPaths";
import { CLINICS } from "../../../../enums/QueryKeys";
import { HOME, RESEND_VERIFICATION_EMAIL } from "../../../../enums/UrlPaths";
import authApi from "../../../../utils/api/authApi";
import { getBearerToken } from "../../../../utils/Functions";
import {
  legalSignUpParser,
  getLegalValueParser,
} from "../../utils/AuthZodValidation";
import PasswordForm from "../form/PasswordForm";
import SignUpFormBody from "../form/SignUpFormBody";
import OrganizationInfo from "./OrganizationInfo";
import RegSubmit from "./RegSubmit";
import { fetchClinicList } from "../../../../enums/queryFunction/Fetch";
import { useLanguageAwareForm } from "../../../../hooks/useLanguageAwareForm";

// Custom hook to create a Memoized Zod Resolver that updates on language change.
// We do this because the validation parser depends on i18n t object to return translated messages.
const useLegalRegResolver = () => {
  const { t } = useTranslation();
  const schema = React.useMemo(() => {
    return getLegalValueParser(t);
  }, [t]);

  return React.useMemo(() => {
    return zodResolver(schema);
  }, [schema]);
};
const SIGN_UP_LEGAL_SERVER = "signUpLegalServer";

export default function LegalReg({ activeStep, getPageErrors }) {
  const { data } = useQuery({
    queryKey: [CLINICS],
    queryFn: fetchClinicList,
    staleTime: Infinity,
  });
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const resolver = useLegalRegResolver();

  const methods = useLanguageAwareForm({
    resolver: resolver,
    mode: "onSubmit",
  });

  const { setError, formState: { errors } } = methods;

  React.useEffect(() => {
    getPageErrors(errors);
  }, [errors, getPageErrors]);

  React.useEffect(() => {
    async function fetch() {
      const isToken = await getBearerToken();
      if (isToken) {
        navigate(HOME);
      }
    }
    fetch();
  });

  const registrationSubmit = async (e) => {
    const data = legalSignUpParser(e);
    data.businessAccount = true;
    try {
      setIsLoading(true);
      await authApi.post(REGISTER, data);
      handleSnackbar("AccountCreated", "success");
      navigate(RESEND_VERIFICATION_EMAIL, {
        state: { email: data.email },
      });
    } catch (error) {
      const errorResponse = error?.response?.data;
      if (typeof errorResponse === "object" && errorResponse !== null) {
        Object.entries(errorResponse).forEach(([field, message]) => {
          setError(field, {
            type: SIGN_UP_LEGAL_SERVER,
            message: message,
          });
        });
      }
      handleSnackbar(error?.response?.data, "error");
    } finally {
      setIsLoading(false);
    }
  };
  const height = "50vh";

  return (
    <React.Fragment>
      <form onSubmit={methods.handleSubmit(registrationSubmit)}>
        <>
          {activeStep == 0 ? (
            <Stack
              mt={2}
              minHeight={height}
              justifyContent={"center"}
              alignItems={"center"}
              spacing={4}
            >
              <TextField
                key={"name"}
                required
                fullWidth
                label={t("Name")}
                size="medium"
                inputProps={{ maxLength: 120, "data-testid": "auth.signup.firstName-input" }}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                {...methods.register("firstName")}
              />
              <TextField
                key={"phoneNumber"}
                required
                fullWidth
                label={t("PhoneNumber")}
                size="medium"
                inputProps={{ maxLength: 120, "data-testid": "auth.signup.phoneNumber-input" }}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
                {...methods.register("phoneNumber")}
              />
              <FormProvider {...methods}>
                <OrganizationInfo />
              </FormProvider>
            </Stack>
          ) : null}
          {activeStep == 1 ? (
            <Stack
              mt={2}
              minHeight={height}
              justifyContent={"center"}
              alignItems={"center"}
              spacing={4}
            >
              <FormProvider {...methods}>
                <SignUpFormBody grid={false} contact={false} oib={false} />
              </FormProvider>
            </Stack>
          ) : null}
          {activeStep == 2 ? (
            <Stack
              mt={2}
              minHeight={height}
              justifyContent={"center"}
              alignItems={"center"}
              spacing={4}
            >
              <TextField
                fullWidth
                required
                key={"email"}
                label={t("Email")}
                size="medium"
                inputProps={{ maxLength: 120, "data-testid": "auth.signup.email-input" }}
                error={!!errors.email}
                helperText={errors.email?.message}
                {...methods.register("email")}
              />
              <TextField
                fullWidth
                required
                key={"usernameF"}
                label={t("Username")}
                size="medium"
                inputProps={{ maxLength: 120, "data-testid": "auth.signup.username-input" }}
                error={!!errors.username}
                helperText={errors.username?.message}
                {...methods.register("username")}
              />
              {/* Clinic Selection Field */}
              <FormControl error={!!errors.clinic} fullWidth>
                <Controller
                  name={"clinic"}
                  control={methods.control}
                  {...methods.register("clinic")}
                  render={({ field }) => (
                    <Autocomplete
                      fullWidth
                      size="medium"
                      defaultValue={""}
                      {...field}
                      value={field.value ?? null}
                      onChange={(e, value) => {
                        field.onChange(value);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      options={data}
                      getOptionLabel={(option) =>
                        `${option.title} - ${option.streetAddress}, ${option.city}`
                      }
                      renderOption={(props, option) => (
                        <Box {...props} key={option.id}>
                          {option.title} - {option.streetAddress}, {option.city}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          error={!!errors?.clinic}
                          label={t("Clinic")}
                          inputProps={{ ...params.inputProps, "data-testid": "auth.signup.clinic-input" }}
                        />
                      )}
                      clearText={t("Clear")}
                      closeText={t("Close")}
                      openText={t("Open")}
                    />
                  )}
                />
                {errors?.clinic?.message && (
                  <FormHelperText>{t("PleaseSelectClinic")}</FormHelperText>
                )}
              </FormControl>
              <FormProvider {...methods}>
                <PasswordForm />
              </FormProvider>
              <RegSubmit isLoading={isLoading} />
            </Stack>
          ) : null}
        </>
      </form>
    </React.Fragment>
  );
}
